// frontend/src/services/chatPrefs.js
// Preferencias de notificación del chat — espejo local de /chat/preferences.
// El backend es la fuente de verdad (también filtra el push del lado servidor);
// este caché alimenta las decisiones de sonido del frontend sin latencia.
import { api } from './api';

const DEFAULTS = {
  sounds_enabled: true,
  push_enabled: true,
  sound_messages: true,
  sound_mentions: true,
  sound_calls: true,
  volume: 80,
  dnd_enabled: false,
  quiet_hours_start: null,
  quiet_hours_end: null,
};

class ChatPrefs {
  constructor() {
    this.global = { ...DEFAULTS };
    this.channels = {}; // channelId -> {mode, muted_until}
    this.loaded = false;
    this.listeners = new Set();
    // hidratación rápida desde localStorage (solo cosmético; el backend manda)
    try {
      const cached = JSON.parse(localStorage.getItem('chatPrefs') || 'null');
      if (cached?.global) { this.global = { ...DEFAULTS, ...cached.global }; this.channels = cached.channels || {}; }
    } catch { /* noop */ }
  }

  onChange(cb) { this.listeners.add(cb); return () => this.listeners.delete(cb); }
  _emit() {
    try { localStorage.setItem('chatPrefs', JSON.stringify({ global: this.global, channels: this.channels })); } catch { /* noop */ }
    this.listeners.forEach((cb) => { try { cb(this); } catch { /* noop */ } });
  }

  async load() {
    try {
      const r = await api.chat.getPreferences();
      this.global = { ...DEFAULTS, ...(r.data?.global || {}) };
      this.channels = r.data?.channels || {};
      this.loaded = true;
      this._emit();
    } catch { /* mantener caché */ }
    return this;
  }

  getGlobal() { return this.global; }

  async updateGlobal(patch) {
    this.global = { ...this.global, ...patch };
    this._emit();
    try {
      const r = await api.chat.updatePreferences(patch);
      this.global = { ...DEFAULTS, ...(r.data?.global || {}) };
      this.channels = r.data?.channels || this.channels;
      this._emit();
    } catch { /* offline: queda el optimista */ }
  }

  getChannelSetting(channelId) {
    return this.channels[channelId] || { mode: 'all', muted_until: null };
  }

  isChannelMuted(channelId) {
    const s = this.getChannelSetting(channelId);
    if (s.mode === 'none') return true;
    if (s.muted_until && new Date(s.muted_until).getTime() > Date.now()) return true;
    return false;
  }

  channelMode(channelId) {
    return this.getChannelSetting(channelId).mode || 'all';
  }

  async setChannelNotification(channelId, mode, muteMinutes = null) {
    try {
      const r = await api.chat.setChannelNotifications(channelId, mode, muteMinutes);
      this.channels[channelId] = { mode: r.data.mode, muted_until: r.data.muted_until };
      this._emit();
      return this.channels[channelId];
    } catch (e) { throw e; }
  }

  inQuietHours() {
    const { quiet_hours_start: s, quiet_hours_end: e } = this.global;
    if (!s || !e) return false;
    const now = new Date();
    const hm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    if (s <= e) return hm >= s && hm < e;
    return hm >= s || hm < e; // cruza medianoche
  }

  /**
   * Decide si un mensaje entrante debe SONAR.
   * Reglas: nunca para mensajes propios; canal silenciado no suena (salvo
   * mención con modo 'mentions'); canal activo+ventana con foco no suena
   * salvo mención directa.
   */
  shouldPlayMessageSound({ channelId, isOwn, isMention, isActiveChannel, windowFocused }) {
    if (isOwn) return null;
    const mode = this.channelMode(channelId);
    const muted = this.isChannelMuted(channelId);
    if (mode === 'none') return null;
    if (muted && !(isMention && mode !== 'none')) return null;
    if (mode === 'mentions' && !isMention) return null;
    if (isActiveChannel && windowFocused && !isMention) return null;
    return isMention ? 'mention' : 'message';
  }

  reset() {
    this.global = { ...DEFAULTS };
    this.channels = {};
    this.loaded = false;
    try { localStorage.removeItem('chatPrefs'); } catch { /* noop */ }
  }
}

const chatPrefs = new ChatPrefs();
export default chatPrefs;
